import { InputMask } from '@components/atoms';
import { UserData, useUsers } from '@hooks';
import { Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';

interface UserFormModalProps {
  open: boolean;
  onCancel: () => void;
  editingUser: UserData | null;
}

export const UserFormModal = ({ open, onCancel, editingUser }: UserFormModalProps) => {
  const [form] = Form.useForm();
  const { createUser, updateUser } = useUsers();
  const isEditing = !!editingUser;

  // Fill the form when editing a user
  useEffect(() => {
    if (editingUser) {
      form.setFieldsValue({
        email: editingUser.email,
        profile: editingUser.profile,
        physicalPerson: {
          name: editingUser.physicalPerson?.name || '',
          phone: editingUser.physicalPerson?.phone || '',
        },
      });
    } else {
      form.resetFields();
    }
  }, [editingUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (isEditing && editingUser) {
        await updateUser.mutateAsync({
          id: editingUser.id,
          data: values,
        });
      } else {
        await createUser.mutateAsync(values);
      }

      form.resetFields();
      onCancel();
    } catch (error) {
      console.error('Form error:', error);
    }
  };

  return (
    <Modal
      title={
        <span className="!font-['Android_101'] !font-normal">
          {isEditing ? 'EDIT USER' : 'ADD USER'}
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={createUser.isPending || updateUser.isPending}
      okText={isEditing ? 'Update' : 'Add'}
      cancelText="CANCEL"
      okButtonProps={{
        className: 'bg-[#E3AF25] !text-black !border-none hover:!bg-[#E3AF25] !font-["Android_101"] hover:!translate-y-[-2px] transition-all duration-200'
      }}
      cancelButtonProps={{
        className: '!font-["Android_101"] !font-normal !text-red-500 !border-red-500 hover:!text-red-500 hover:!border-red-500 hover:!bg-transparent !uppercase hover:!translate-y-[-2px] transition-all duration-200 [&_span]:!text-red-500 [&_span]:hover:!text-red-500'
      }}
      className="[&_.ant-modal]:!rounded-none [&_.ant-modal-content]:!bg-zinc-800 [&_.ant-modal-content]:!text-white [&_.ant-modal-content]:!rounded-none [&_.ant-modal-header]:!bg-zinc-800 [&_.ant-modal-header]:!border-b-zinc-700 [&_.ant-modal-header]:!rounded-none [&_.ant-modal-title]:!text-white [&_.ant-modal-footer]:!border-t-zinc-700 [&_.ant-modal-footer]:!rounded-none [&_.ant-modal-close-x]:!text-[#E3AF25] [&_.ant-modal-close-x]:hover:!text-[#E3AF25] [&_.ant-modal-footer_.ant-btn-default]:!text-red-500 [&_.ant-modal-footer_.ant-btn-default_span]:!text-red-500 [&_.ant-modal-footer_.ant-btn-default:hover_span]:!text-red-500"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          profile: 'user',
          physicalPerson: {
            name: '',
            phone: '',
            cpf: '',
          },
        }}
        className="!font-['Barlow']"
      >
        <Form.Item
          name="email"
          label={<span className="!text-white">Email</span>}
          rules={[
            { required: true, message: 'Please enter an email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            placeholder="User email"
            className="!bg-zinc-700 !text-white !border-zinc-600"
          />
        </Form.Item>

        <Form.Item
          name="profile"
          label={<span className="!text-white">Profile</span>}
          rules={[{ required: true, message: 'Please select a profile' }]}
        >
          <Select
            placeholder="Select profile"
            className="!bg-zinc-700 !text-white [&_.ant-select-selector]:!bg-zinc-700 [&_.ant-select-selector]:!border-zinc-600"
          >
            <Select.Option value="admin">Administrator</Select.Option>
            <Select.Option value="manager">Manager</Select.Option>
            <Select.Option value="user">User</Select.Option>
          </Select>
        </Form.Item>

        <div className="!mt-4 !mb-2">
          <h4 className="!text-white !font-['Barlow'] !font-medium">Personal Information</h4>
        </div>

        <Form.Item
          label={<span className="!text-white">Name</span>}
          name={['physicalPerson', 'name']}
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input
            placeholder="Full name"
            className="!bg-zinc-700 !text-white !border-zinc-600"
          />
        </Form.Item>

        <Form.Item
          label={<span className="!text-white">Phone</span>}
          name={['physicalPerson', 'phone']}
        >
          <InputMask
            mask="usphone"
            placeholder="(000) 000-0000"
            className="!bg-zinc-700 !text-white !border-zinc-600"
          />
        </Form.Item>
        {/* <Form.Item
          label={<span className="!text-white"> SSN</span>}
          name={['physicalPerson', 'cpf']}
          rules={[{ required: true, message: 'Please enter a valid SSN' }]}
        >
          <Input
            placeholder="000.000.000-00"
            className="!bg-zinc-700 !text-white !border-zinc-600"
          />
        </Form.Item> */}
      </Form>
    </Modal>
  );
};
